import { animate, state, style, transition, trigger } from '@angular/animations';
import { Component, EventEmitter, Input, OnDestroy, OnInit, Output, ElementRef, ViewChild } from '@angular/core';
import { FormGroup, FormControl, Validators, ValidatorFn, ValidationErrors, AbstractControl } from '@angular/forms';
import { AuthFormNotificationType, AuthFormType } from '@shared/models/auth.interface';

import { AuthService } from '@shared/services/auth/auth.service';
import { AppService } from '@shared/services/core';
import { LoadingService } from '@shared/services/core/loading.service';
import { ActivatedRoute,Router  } from '@angular/router';
import { Subscription } from 'rxjs';
import { SessionService } from '@shared/services/auth/session.service';

const demoAnimation = trigger('demoAnimation', [
  transition(':enter', [
    style({ transform: 'translateX(40%)' }),
    animate('200ms', style({ transform: 'translateX(0%)' }))
  ]),
])

@Component({
  selector: 'app-password-recovery-form',
  templateUrl: './password-recovery-form.component.html',
  styleUrls: ['./password-recovery-form.component.scss'],
  animations: [demoAnimation]
})
export class PasswordRecoveryFormComponent implements OnInit, OnDestroy {
  @Input() accessCode: string = '';
  @Output() notify = new EventEmitter<AuthFormNotificationType | null>();
  @Output() formChange = new EventEmitter<AuthFormType>();

  passwordRecoverySubscription: Subscription | undefined;
  formValueChangesSubscription!: Subscription;
  fieldUsedForRecovery: "EMAILID" | "USERNAME" | undefined;
  formResetted: boolean = false;

  // using a separate flag for validation because the condition for form validity is a bit different (i.e. mutually exclusive fields).
  formIsValid: boolean = false;
  
  // New properties for checkbox-driven captcha flow
  isNotRobotChecked: boolean = false; // Track "I'm not a robot" checkbox state
  showCaptcha: boolean = false; // Control captcha visibility
  captchaCompleted: boolean = false; // Track if captcha is fully entered (6 chars)
  
  passwordRecoveryForm: FormGroup = new FormGroup({
    Captcha: new FormControl(''), // Remove required validator initially
    ClientId: new FormControl('', [clientIdFieldValidator()]),
    NotRobotCheckbox: new FormControl(false) // Add checkbox control
  })

  @ViewChild('captchaCanvas', { static: false }) canvasRef!: ElementRef<HTMLCanvasElement>;
  captchaText = '';
  userInput = '';
  valid = true;
  ctx!: CanvasRenderingContext2D;

  private fonts = ['Arial', 'Courier', 'Georgia', 'Verdana', 'Tahoma'];

  constructor(private authService: AuthService, private loadingService: LoadingService, private appService: AppService, private router: Router, private sessionService: SessionService) { }

  ngOnInit(): void {
    // Subscribe to form value changes to manage form validation and captcha flow
    this.formValueChangesSubscription = this.passwordRecoveryForm.valueChanges
    .subscribe(_ => {
      // Check if username is provided and valid
      let usernameProvided = !!(this.passwordRecoveryForm.get('ClientId')?.value);
      let usernameValid = !!(this.passwordRecoveryForm.get('ClientId')?.valid);
      
      // Check captcha completion (6 characters)
      let captchaValue = this.passwordRecoveryForm.get('Captcha')?.value || '';
      this.captchaCompleted = captchaValue.length === 6;
      
      // Form is valid when: username is valid, checkbox is checked, and captcha is completed
      this.formIsValid = usernameProvided && usernameValid && this.isNotRobotChecked && this.captchaCompleted;

      if (this.formResetted) this.formResetted = false;
      else this.notify.next(null);
    });

    // Initialize canvas context but don't generate captcha on load
  //  this.ctx = this.canvasRef.nativeElement.getContext('2d')!;
  }

  // Handle "I'm not a robot" checkbox click
  onNotRobotCheckboxChange(checked: boolean): void {
    this.isNotRobotChecked = checked;
    
    if (checked) {
      // Show captcha when checkbox is checked
      this.showCaptcha = true;
      // Use setTimeout to ensure DOM is updated before generating captcha
      setTimeout(() => {
        this.initializeCanvasContext();
        this.generateCaptcha();
      }, 100);
      // Add required validator to captcha field
      this.passwordRecoveryForm.get('Captcha')?.setValidators([Validators.required]);
      this.passwordRecoveryForm.get('Captcha')?.updateValueAndValidity();
    } else {
      // Hide captcha when checkbox is unchecked
      this.showCaptcha = false;
      this.captchaCompleted = false;
      // Remove required validator from captcha field
      this.passwordRecoveryForm.get('Captcha')?.clearValidators();
      this.passwordRecoveryForm.get('Captcha')?.updateValueAndValidity();
      this.passwordRecoveryForm.get('Captcha')?.setValue('');
    }
  }

  // Initialize canvas context safely
  private initializeCanvasContext(): void {
    if (this.canvasRef && this.canvasRef.nativeElement) {
      this.ctx = this.canvasRef.nativeElement.getContext('2d')!;
    }
  }

  generateCaptcha() {
    // Ensure canvas and context are available
    if (!this.canvasRef || !this.canvasRef.nativeElement) {
      console.error('Canvas element not available');
      return;
    }

    const canvas = this.canvasRef.nativeElement;
    if (!this.ctx) {
      this.ctx = canvas.getContext('2d')!;
    }
    
    const ctx = this.ctx;
    if (!ctx) {
      console.error('Canvas context not available');
      return;
    }

    // Clear canvas
    ctx.clearRect(0, 0, canvas.width, canvas.height);

    // Background - Original logic
    ctx.fillStyle = this.randomColor(180, 255);
    ctx.fillRect(0, 0, canvas.width, canvas.height);

    // Generate 6-character captcha - Original logic
    const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZabcdefghijkmnopqrstuvwxyz23456789';
    this.captchaText = Array.from({ length: 6 }, () => chars[Math.floor(Math.random() * chars.length)]).join('');

    // Draw CAPTCHA - Original logic restored
    for (let i = 0; i < this.captchaText.length; i++) {
      const char = this.captchaText[i];
      ctx.font = `${20 + Math.random() * 10}px ${this.fonts[Math.floor(Math.random() * this.fonts.length)]}`;
      ctx.fillStyle = this.randomColor(10, 150);
      ctx.save();
      ctx.translate(20 + i * 22, 30);
      ctx.rotate((Math.random() - 0.5) * 0.6);
      ctx.fillText(char, 0, 0);
      ctx.restore();
    }

    // Noise lines - Original logic restored
    for (let i = 0; i < 5; i++) {
      ctx.strokeStyle = this.randomColor(50, 160);
      ctx.beginPath();
      ctx.moveTo(Math.random() * canvas.width, Math.random() * canvas.height);
      ctx.lineTo(Math.random() * canvas.width, Math.random() * canvas.height);
      ctx.stroke();
    }

    // Dots - Original logic restored
    for (let i = 0; i < 50; i++) {
      ctx.fillStyle = this.randomColor(0, 255);
      ctx.beginPath();
      ctx.arc(Math.random() * canvas.width, Math.random() * canvas.height, 1, 0, Math.PI * 2);
      ctx.fill();
    }

    this.userInput = '';
    this.valid = true;
  }

  playAudio() {
    if ('speechSynthesis' in window) {
      const utterance = new SpeechSynthesisUtterance(this.captchaText.split('').join(' '));
      utterance.rate = 0.8;
      utterance.pitch = 1.1;
      utterance.lang = 'en-US';
      speechSynthesis.speak(utterance);
    } else {
      alert('Sorry, your browser does not support audio CAPTCHA.');
    }
  }
  CaptchaMismatchError = false;
  
  // Main submit function - validates captcha and processes password recovery
  verify() {
    this.userInput = this.passwordRecoveryForm.get('Captcha')?.value;
    this.valid = this.userInput === this.captchaText;

    if (!this.valid) {
      // On captcha mismatch: keep captcha visible and checkbox checked
      this.generateCaptcha();
      this.passwordRecoveryForm.get("Captcha")?.setErrors({ captchaMismatch: true });
      this.CaptchaMismatchError = true;
      // Keep showCaptcha = true and isNotRobotChecked = true
    } else {
      // On successful captcha: proceed with password recovery
      this.recoverPassword();
      this.userInput = '';
      this.CaptchaMismatchError = false;
    }
  }

  private randomColor(min: number, max: number): string {
    const r = min + Math.floor(Math.random() * (max - min));
    const g = min + Math.floor(Math.random() * (max - min));
    const b = min + Math.floor(Math.random() * (max - min));
    return `rgb(${r},${g},${b})`;
  }
  
  recoverPassword() {
    // Validate required fields
    if(!this.passwordRecoveryForm.get("ClientId")?.value){  
      this.passwordRecoveryForm.get("Captcha")?.setErrors({ required: true });
      this.passwordRecoveryForm.get("ClientId")?.setErrors({ required: true });
      return;
    }
    
    // Validate captcha is entered
    if(!this.passwordRecoveryForm.get("Captcha")?.value){
      this.passwordRecoveryForm.get("Captcha")?.setErrors({ required: true });
      return;
    }
    
    this.fieldUsedForRecovery = "USERNAME";
    this.notify.next(null);
    
    try {
      const form = this.passwordRecoveryForm.value;
      this.loadingService.showLoading();
      this.accessCode = this.accessCode;
      const url = window.location.origin+'/setpassword?tag=';
      const productVer=this.sessionService.getNonEncryptedStorageMapping("productVer");
      if(String(productVer) === "false")
      {
        this.passwordRecoverySubscription = this.authService.recoverPassword({ 
          ...form, 
          AccessCode: this.accessCode, 
          //URL: url 
          }).subscribe({
          next: (response) => {
            if (response?.Data) {
              // On successful email send: show success message first
              this.notify.next("PASS_RECOVERY_SUCCESS");
              this.formResetted = true;
              
              // Wait 3 seconds to show success message, then reset form and navigate
              setTimeout(() => {
                this.resetFormToInitialState();
                this.router.navigate(['/login']);
              }, 3000);
            } else {
              // On error: keep captcha visible and checkbox checked
              if(this.fieldUsedForRecovery === "EMAILID") this.notify.next('PASS_RECOVERY_ERROR_WITH_EMAIL')
              else if(this.fieldUsedForRecovery === "USERNAME") this.notify.next('PASS_RECOVERY_ERROR_WITH_USERNAME')
              else this.notify.next("UNKNOWN_ERROR");
              // Generate new captcha but keep it visible
              this.generateCaptcha();
            }
            this.loadingService.stopLoading();
          },
          error: (error) => {
            console.error('Password recovery error:', error);
            this.notify.next("UNKNOWN_ERROR");
            this.generateCaptcha();
            this.loadingService.stopLoading();
          }
          });
      }
      else
      {
         this.passwordRecoverySubscription = this.authService.recoverPasswordv2({ 
        ...form, 
        AccessCode: this.accessCode, 
        URL: url 
        }).subscribe({
        next: (response) => {
          if (response?.Data) {
            // On successful email send: show success message first
            this.notify.next("PASS_RECOVERY_SUCCESS");
            this.formResetted = true;
            
            // Wait 3 seconds to show success message, then reset form and navigate
            setTimeout(() => {
              this.resetFormToInitialState();
              this.router.navigate(['/login']);
            }, 3000);
          } else {
            // On error: keep captcha visible and checkbox checked
            if(this.fieldUsedForRecovery === "EMAILID") this.notify.next('PASS_RECOVERY_ERROR_WITH_EMAIL')
            else if(this.fieldUsedForRecovery === "USERNAME") this.notify.next('PASS_RECOVERY_ERROR_WITH_USERNAME')
            else this.notify.next("UNKNOWN_ERROR");
            // Generate new captcha but keep it visible
            this.generateCaptcha();
          }
          this.loadingService.stopLoading();
        },
        error: (error) => {
          console.error('Password recovery error:', error);
          this.notify.next("UNKNOWN_ERROR");
          this.generateCaptcha();
          this.loadingService.stopLoading();
        }
        });
      }
    } catch (error) {
      console.error('Password recovery exception:', error);
      this.notify.next("UNKNOWN_ERROR");
      this.loadingService.stopLoading();
    }
  }
  
  // Reset form to initial state (success scenario)
  private resetFormToInitialState(): void {
    this.passwordRecoveryForm.reset();
    this.isNotRobotChecked = false;
    this.showCaptcha = false;
    this.captchaCompleted = false;
    this.passwordRecoveryForm.get('NotRobotCheckbox')?.setValue(false);
    this.passwordRecoveryForm.get('ClientId')?.setValue('');
    this.passwordRecoveryForm.get('Captcha')?.setValue('');
    // Remove captcha required validator
    this.passwordRecoveryForm.get('Captcha')?.clearValidators();
    this.passwordRecoveryForm.get('Captcha')?.updateValueAndValidity();
  }

  disableAlternativeField(currentField: string, alternativeField: string) {
    this.passwordRecoveryForm.controls[currentField].enable();
    this.passwordRecoveryForm.controls[alternativeField].reset();
    this.passwordRecoveryForm.controls[alternativeField].disable();
  }

  enableAllFields() {
    this.passwordRecoveryForm.enable();
   
    const clientIdField = this.passwordRecoveryForm.controls['ClientId'];
    
    if (clientIdField.touched && !clientIdField.value)
      this.passwordRecoveryForm.get("ClientId")?.setErrors({ required: true });

    this.passwordRecoveryForm.touched
  }

  ngOnDestroy(): void {
    this.formValueChangesSubscription.unsubscribe();
    this.passwordRecoverySubscription?.unsubscribe();
  }
}

function clientIdFieldValidator(): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    const clientId = control.value;

    if (clientId && clientId.length > 0 && clientId.length < 10) return { invalidLength: true }
    else return null;
  }
}